import {
    ClickEvent, Image, LookupModelSearchEvent, LookupModelSelectionEvent, TableRow, TableRowDisplayEvent, Textbox
} from "@mcleod/components";
import { AutogenLayoutCallRecordOpenItemTable } from "./autogen/AutogenLayoutCallRecordOpenItemTable";
import { Api, Currency, DatePart, DateUtil, ModelRow } from "@mcleod/core";
import { OrderTabView } from "@mcleod/dispatch";

export class CallRecordOpenItemTable extends AutogenLayoutCallRecordOpenItemTable {
    public defaultBillDueDays: number;
    public callDate: Date;
    private _parentCustomerId: string;
    private _customerIds: string[] = null;

    get parentCustomerId(): string {
        return this._parentCustomerId;
    }

    set parentCustomerId(value: string) {
        this._parentCustomerId = value;
        this._customerIds = null;
    }

    get customerIds(): string[] {
        return this._customerIds;
    }

    set customerIds(value: string[]) {
        this._customerIds = value;
    }

    async getBridgeCustomerIds() {
        const id = this.parentCustomerId ?? null;
        if (id != null && this.customerIds === null) {
            const response = await Api.post("lme/ar/get-bridge-customer-ids", { parent_customer_id: id });
            this.customerIds = response?.data[0]?.customer_ids as string[];
            this.customerIds.push(id);
        }
    }

    tableCallRecOpenItemOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;

        if (modelRow && !tableRow.isQuickAddRow()) {
            const invoiceNumber = modelRow.getString("invoice_no_string", null);
            const orderId = modelRow.getString("order_id", null);
            const consigneeRefNo = modelRow.getString("consignee_refno", null);
            const referenceNumber = modelRow.getString("reference_number", null);

            modelRow.setLookupModelData("invoice_no_string", { invoice_no_string: invoiceNumber });
            modelRow.setLookupModelData("order_id", { order_id: orderId });
            modelRow.setLookupModelData("consignee_refno", { consignee_refno: consigneeRefNo });
            modelRow.setLookupModelData("reference_number", { reference_number: referenceNumber });

            const imageViewImages = tableRow.findComponentById("imageViewImages") as Image;
            if (imageViewImages && orderId) {
                imageViewImages.visible = true;
                imageViewImages.busy = true;
                Api.search("common/imaging/list", {
                    row_id: orderId,
                    row_type: "O"
                }).then(response => {
                    imageViewImages.visible = response.data.length > 0;
                }).catch(error => {
                    imageViewImages.visible = false;
                })
                .finally(() => imageViewImages.busy = false);
            }

            if (modelRow.isNull("entered_date")) {
                modelRow.set("entered_date", this.callDate ?? null);
            }

            if (modelRow.isNull("days_past_due")) {
                modelRow.set("days_past_due", this.calculateDaysPastDue(modelRow));
            }

            tableRow.displayComponentValues();
        }
    }

    calculateDaysPastDue(modelRow: ModelRow): number {
        const customerId = modelRow.getString("customer_id", null);
        const billDate = modelRow.getDate("bill_date", null);
        const agingDate = modelRow.getDate("entered_date", this.callDate);
        let daysPastDue = 0;
        if (customerId && billDate) {
            const billDueDays = modelRow.getNumber("bill_due_days", this.defaultBillDueDays);
            const agingDays = DateUtil.dateDiff(DatePart.DAY, agingDate, billDate);
            daysPastDue = agingDays - billDueDays;
        }

        return Math.max(daysPastDue, 0);
    }

    imageViewImagesOnClick(event: ClickEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target as Image);
        const modelRow = tableRow?.data as ModelRow;
        const orderId = modelRow?.getString("order_id", null);
        if (orderId) {
            OrderTabView.slideUp({ key: orderId, additionalProps: { selectedOrderTab: "tabImages" } });
        }
    }

    // START - Before lookup model search listeners
    textboxInvoiceNoStringBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        this.beforeLookupModelSearch(event);
    }

    textboxOrderIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        this.beforeLookupModelSearch(event);
    }

    textboxConsigneeRefnoBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        this.beforeLookupModelSearch(event);
    }

    textboxReferenceNumberBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        this.beforeLookupModelSearch(event);
    }

    beforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.record_type = "I";
        if (this.customerIds) {
            event.filter.customer_id = "in " + this.customerIds.join(",");
        }
    }
    // END - Before lookup model search listeners

    // START - Lookup model selection listeners
    textboxInvoiceNoStringOnLookupModelSelection(event: LookupModelSelectionEvent) {
        this.populateRowFromSelection(event);
    }

    textboxOrderIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        this.populateRowFromSelection(event);
    }

    textboxConsigneeRefnoOnLookupModelSelection(event: LookupModelSelectionEvent) {
        this.populateRowFromSelection(event);
    }

    textboxReferenceNumberOnLookupModelSelection(event: LookupModelSelectionEvent) {
        this.populateRowFromSelection(event);
    }

    populateRowFromSelection(event: LookupModelSelectionEvent) {
        const selectedRow = event.selection;
        const textbox = event.target as Textbox;
        if (selectedRow && textbox) {
            const tableRow = TableRow.getContainingTableRow(textbox);
            const modelRow = tableRow?.data as ModelRow;
            if (modelRow) {
                if (!selectedRow.isNull("amount")) {
                    modelRow.updateCurrency("amount", new Currency(selectedRow.getCurrency("amount")));
                }
                modelRow.set("bill_date", selectedRow.getDate("bill_date", null));
                modelRow.set("bill_due_days", selectedRow.getNumber("bill_due_days"));
                const consigneeRefNo = selectedRow.getString("consignee_refno");
                modelRow.setValueAndLookupModelData("consignee_refno", consigneeRefNo, { consignee_refno: consigneeRefNo });
                modelRow.set("customer_id", selectedRow.getString("customer_id"));
                const invoiceNoString = selectedRow.getString("invoice_no_string");
                modelRow.setValueAndLookupModelData("invoice_no_string", invoiceNoString, { invoice_no_string: invoiceNoString });
                modelRow.set("open_item_id", selectedRow.getString("id"));
                const orderId = selectedRow.getString("order_id");
                modelRow.setValueAndLookupModelData("order_id", orderId, { order_id: orderId });
                modelRow.set("record_type", selectedRow.getString("record_type"));
                const referenceNumber = selectedRow.getString("reference_number");
                modelRow.setValueAndLookupModelData("reference_number", referenceNumber, { reference_number: referenceNumber });
                modelRow.set("source", selectedRow.getString("source"));
                modelRow.set("summary_billing", selectedRow.getString("summary_billing"));

                if (tableRow.isQuickAddRow()) {
                    modelRow.set("entered_date", new Date());
                    modelRow.set("days_past_due", this.calculateDaysPastDue(modelRow));
                }

                tableRow?.displayComponentValues();
            }
        }
    }
    // END - Lookup model selection listeners
}
